import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import QueueOutlinedIcon from '@mui/icons-material/QueueOutlined';
import { useNavigate } from 'react-router-dom';
import ConfirmBox from '../../../components/FlowBuilder/Common/ConfirmBox/index.tsx';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import { TablePagination } from '../../../components/ui';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider.jsx';
import { dateFormate } from '../../../utils/helper.jsx';
import TemplateColumnHeader from '../components/list/TemplateColumnHeader';
import TemplateDataTable from '../components/shared/TemplateDataTable';
import StatusChip from '../components/ui/StatusChip';
import TemplateQueueFilters from '../components/queue/TemplateQueueFilters';
import TemplateQueueRowActions from '../components/queue/TemplateQueueRowActions';
import useTemplateSx from '../hooks/useTemplateSx';
import { templateService } from '../services/templateService';
import { TEMPLATE_BASE_PATH, TEMPLATE_LIST_PAGE_SIZE } from '../constants/templateApi';
import {
  DEFAULT_QUEUE_FILTERS,
  applyQueueFilterPatch,
  clearQueueFilterChip,
} from '../utils/templateQueueFilters';

const STATUS_LABELS = {
  draft: 'Draft',
  pending: 'Pending',
  processing: 'Processing',
  sent: 'Sent',
  failed: 'Failed',
  scheduled: 'Scheduled',
  queued: 'Queued',
  running: 'Running',
  done: 'Done',
  paused: 'Paused',
};

function actionFailure(error) {
  const data = error?.response?.data?.data;
  if (typeof data === 'string') return data;
  if (typeof data?.general_error === 'string') return data.general_error;
  return 'Could not update the queue';
}

function confirmText(type, row) {
  const name = row?.template_name || 'this send';
  const org = row?.organisation_name || row?.company_id || 'this organisation';
  if (type === 'remove') {
    return `This permanently removes the unsent draft for ${org} / ${name}. ${row?.draft || 0} draft rows will be deleted.`;
  }
  if (type === 'retry') {
    return row?.queue_status === 'paused'
      ? `${row?.failed || 0} failed rows for ${name} stay held until you resume. Sent rows stay sent.`
      : `${row?.failed || 0} failed rows for ${name} will be sent again. Sent rows stay sent.`;
  }
  if (type === 'pause') {
    return `Pending messages for ${name} (${org}) will wait. Messages already processing will finish.`;
  }
  return `Held messages for ${name} will be queued again.`;
}

function readListPayload(res) {
  if (res?.status !== 200 || res?.data?.status === false) {
    const message = res?.data?.general_error || res?.data?.data?.general_error || res?.data?.data || 'Failed to load template queue';
    return {
      results: [],
      total: 0,
      error: typeof message === 'string' ? message : 'Failed to load template queue',
    };
  }
  const body = res?.data ?? {};
  const payload = body.results || body.total_count != null ? body : (body.data ?? {});
  return {
    results: payload.results ?? [],
    total: payload.total_count ?? 0,
    error: null,
  };
}

function CountCell({ cell }) {
  return cell.getValue() ?? 0;
}

function StackedCell({ primary, secondary }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, width: '100%' }}>
      <Typography
        component="span"
        sx={{
          fontSize: '0.8125rem',
          fontWeight: 600,
          lineHeight: 1.3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}
      >
        {primary || '—'}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: '0.6875rem',
          color: 'text.secondary',
          lineHeight: 1.3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          mt: 0.25,
        }}
      >
        {secondary || '—'}
      </Typography>
    </Box>
  );
}

const stackedCellProps = {
  sx: {
    overflow: 'hidden',
    whiteSpace: 'normal',
    textAlign: 'left',
    justifyContent: 'flex-start',
  },
};

export default function TemplateQueuePage() {
  return (
    <FeatureThemeProvider>
      <TemplateQueuePageContent />
    </FeatureThemeProvider>
  );
}

function TemplateQueuePageContent() {
  const styles = useTemplateSx();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ ...DEFAULT_QUEUE_FILTERS });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const loadQueue = useCallback(async (nextFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await templateService.getTemplateQueue(nextFilters);
      const payload = readListPayload(res);
      setRows(payload.results);
      setTotal(payload.total);
      setError(payload.error);
    } catch {
      setRows([]);
      setTotal(0);
      setError('Failed to load template queue');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue(filters);
  }, [filters, loadQueue]);

  const runPendingAction = async (confirmed) => {
    const action = pendingAction;
    setPendingAction(null);
    if (!confirmed || !action?.row?.job_id) return;
    setActionError(null);
    const calls = {
      remove: templateService.discardTemplateQueue,
      retry: templateService.retryFailedTemplateQueue,
      pause: templateService.pauseTemplateQueue,
      resume: templateService.resumeTemplateQueue,
    };
    try {
      const res = await calls[action.type](action.row.job_id);
      if (res?.status !== 200 || res?.data?.status === false) {
        setActionError(actionFailure({ response: res }));
        return;
      }
      loadQueue(filters);
    } catch (err) {
      setActionError(actionFailure(err));
    }
  };

  const updateFilters = (patch) => {
    if (patch?.clear) {
      setFilters((prev) => clearQueueFilterChip(prev, patch.clear));
      return;
    }
    setFilters((prev) => applyQueueFilterPatch(prev, patch));
  };

  const renderHeader = (label) => () => <TemplateColumnHeader>{label}</TemplateColumnHeader>;

  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...DEFAULT_QUEUE_FILTERS,
      limit: prev.limit,
    }));
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'template_name',
      header: 'Template',
      Header: renderHeader('Template'),
      enableSorting: false,
      size: 180,
    },
    {
      accessorKey: 'organisation_name',
      header: 'Organisation',
      Header: renderHeader('Organisation'),
      enableSorting: false,
      size: 200,
      muiTableBodyCellProps: stackedCellProps,
      Cell: ({ row }) => (
        <StackedCell
          primary={row.original.organisation_name || row.original.company_id}
          secondary={row.original.bot_number}
        />
      ),
    },
    {
      accessorKey: 'created_by',
      header: 'Shared by',
      Header: renderHeader('Shared by'),
      enableSorting: false,
      size: 190,
      muiTableBodyCellProps: stackedCellProps,
      Cell: ({ row }) => (
        <StackedCell
          primary={row.original.created_by}
          secondary={row.original.created_at
            ? dateFormate(row.original.created_at, 'DD MMM YYYY, hh:mm A')
            : ''}
        />
      ),
    },
    {
      accessorKey: 'scheduled_at',
      header: 'Scheduled at',
      Header: renderHeader('Scheduled at'),
      enableSorting: false,
      size: 170,
      Cell: ({ cell }) => (
        cell.getValue() ? dateFormate(cell.getValue(), 'DD MMM YYYY, hh:mm A') : '—'
      ),
    },
    { accessorKey: 'draft', header: 'Draft', Header: renderHeader('Draft'), enableSorting: false, size: 80, Cell: CountCell },
    { accessorKey: 'pending', header: 'Pending', Header: renderHeader('Pending'), enableSorting: false, size: 90, Cell: CountCell },
    { accessorKey: 'processing', header: 'Processing', Header: renderHeader('Processing'), enableSorting: false, size: 110, Cell: CountCell },
    { accessorKey: 'sent', header: 'Sent', Header: renderHeader('Sent'), enableSorting: false, size: 80, Cell: CountCell },
    { accessorKey: 'failed', header: 'Failed', Header: renderHeader('Failed'), enableSorting: false, size: 80, Cell: CountCell },
    { accessorKey: 'paused', header: 'Paused', Header: renderHeader('Paused'), enableSorting: false, size: 90, Cell: CountCell },
    {
      accessorKey: 'queue_status',
      header: 'Status',
      Header: renderHeader('Status'),
      enableSorting: false,
      size: 120,
      Cell: ({ cell }) => (
        <StatusChip
          value={cell.getValue()}
          label={STATUS_LABELS[cell.getValue()] || cell.getValue()}
        />
      ),
    },
    {
      accessorKey: 'actions',
      header: 'Action',
      Header: renderHeader('Action'),
      size: 72,
      minSize: 72,
      maxSize: 72,
      grow: false,
      muiTableHeadCellProps: {
        sx: styles.actionColumnHeadCell,
      },
      muiTableBodyCellProps: {
        sx: styles.actionColumnBodyCell,
      },
      Cell: ({ row }) => (
        <TemplateQueueRowActions
          row={row.original}
          onView={() => navigate(`${TEMPLATE_BASE_PATH}/template-queue/${row.original.job_id}`, {
            state: { job: row.original },
          })}
          onRemove={() => setPendingAction({ type: 'remove', row: row.original })}
          onRetryFailed={() => setPendingAction({ type: 'retry', row: row.original })}
          onPause={() => setPendingAction({ type: 'pause', row: row.original })}
          onResume={() => setPendingAction({ type: 'resume', row: row.original })}
        />
      ),
    },
  ], [navigate, styles.actionColumnBodyCell, styles.actionColumnHeadCell]);

  return (
    <Box sx={{ ...styles.pageRoot, minHeight: 0 }}>
      <DynamicBreadcrumbs
        items={[
          { label: <IconButton color="primary"><HomeIcon /></IconButton>, href: '/' },
          { label: 'Template Queue' },
        ]}
        onBreadcrumbClick={(_event, item) => {
          if (item?.href) navigate(item.href);
        }}
      />

      <Box sx={{ ...styles.pageHeader, mt: { xs: 1.8, sm: 1.2 } }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <QueueOutlinedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant="h5" sx={styles.pageTitle}>
              Template Queue
            </Typography>
          </Box>
          <Typography variant="body2" sx={styles.pageSubtitle}>
            Shared template sends, with live job status
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.dashboardSurface}>
        <TemplateQueueFilters
          filters={filters}
          onChange={updateFilters}
          onClear={handleClearFilters}
        />

        {error ? (
          <Typography color="error" sx={{ mb: 2, px: { xs: 1, sm: 1.5 } }}>{error}</Typography>
        ) : null}
        {actionError ? (
          <Typography color="error" sx={{ mb: 2, px: { xs: 1, sm: 1.5 } }}>{actionError}</Typography>
        ) : null}

        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
          <TemplateDataTable
            columns={columns}
            data={rows}
            getRowId={(row) => row.job_id}
            layoutMode="grid"
            enableColumnPinning
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableHiding={false}
            enableBottomToolbar={false}
            enablePagination={false}
            initialState={{ density: 'compact' }}
            state={{
              isLoading,
              columnPinning: { right: ['actions'] },
            }}
          />
        </Box>
      </Box>
      <TablePagination
        page={filters.page}
        pageSize={filters.limit || TEMPLATE_LIST_PAGE_SIZE}
        totalCount={total}
        onPageChange={(nextPage) => updateFilters({ page: nextPage })}
        onPageSizeChange={(nextPageSize) => updateFilters({
          limit: Number(nextPageSize) || TEMPLATE_LIST_PAGE_SIZE,
          page: 0,
        })}
        sx={{ mt: 2 }}
      />
      <ConfirmBox
        open={Boolean(pendingAction)}
        onClose={runPendingAction}
        options={{
          title: {
            remove: 'Remove draft queue',
            retry: 'Retry failed rows',
            pause: 'Pause send',
            resume: 'Resume send',
          }[pendingAction?.type] || 'Confirm',
          text: confirmText(pendingAction?.type, pendingAction?.row),
          confirmButtonText: {
            remove: 'Remove',
            retry: 'Retry failed',
            pause: 'Pause',
            resume: 'Resume',
          }[pendingAction?.type] || 'Confirm',
          cancelButtonText: 'Cancel',
          confirmButtonColor: pendingAction?.type === 'remove' ? 'error' : 'primary',
        }}
      />
    </Box>
  );
}
